import qs from "qs";
import axios from "axios";
import { getToken, isTrue, toNumber } from "./service";
import _ from "underscore";
import { QuestionItem } from "../pages/CourseDetail/ExerciseTopics/components/TopicCloneModal/Components/TopicPreview";

export enum BaseQuestionType {
    SingleChoice = 1,
    MultipleChoice = 2, // 多选
    Judge = 3, // 判断
    Subjective = 4, // 主观
    Fill = 5, // 填空
    Mixed = 6 // 阅读理解
}

export const BaseQuestionTypeMap = {
    [BaseQuestionType.SingleChoice]: "单选题",
    [BaseQuestionType.MultipleChoice]: "多选题",
    [BaseQuestionType.Judge]: "判断题",
    [BaseQuestionType.Subjective]: "主观题",
    [BaseQuestionType.Fill]: "填空题",
    [BaseQuestionType.Mixed]: "阅读理解"
};

export interface OptionsType {
    id?: string;
    key?: number;
    questionId?: string;
    description: string;
    isRight: boolean;
}

// 填空题选项
export interface BlankType {
    id?: string;
    description: string;
}

interface QuestionBase {
    id?: string; // 题ID (新增不传，修改传)
    paperId: string; // 练习id
    queTypeId?: BaseQuestionType; // 题类型1:单选题2:多选3:判断4:主观题5:填空题6阅读理解
    score: number; // 分数
    contentText: string; // 题干
    sort?: number;
}

// 单选题型
export interface SingleChoiceType extends QuestionBase {
    queTypeId: BaseQuestionType.SingleChoice;
    options: OptionsType[];
}

// 多选题型
interface MultipleChoiceType extends QuestionBase {
    queTypeId: BaseQuestionType.MultipleChoice;
    options: OptionsType[];
}

// 判断题型
interface JudgeType extends QuestionBase {
    queTypeId: BaseQuestionType.Judge;
    judgeOptions: OptionsType[];
}

// 主观题型
interface SubjectiveType extends QuestionBase {
    queTypeId: BaseQuestionType.Subjective;
    analysis: string; // 参考答案
}

// 填空题型
interface FillType extends QuestionBase {
    queTypeId: BaseQuestionType.Fill;
    blanks: BlankType[];
}

// 其他题型
interface MixedType extends QuestionBase {
    queTypeId: number;
    questions: SimpleQuestionType[];
}

type SimpleQuestionType =
    | SingleChoiceType
    | MultipleChoiceType
    | JudgeType
    | SubjectiveType
    | FillType;
export type QuestionType = SimpleQuestionType | MixedType;

interface optionModelListItem {
    id: number; //选项id
    questionId: number; //题id
    description: string; //选项描述
    isRight: BooleanValue; //是否正确0:不是 1:正确
}

interface questionModelListItem {
    id: number; //小题id
    queTypeId: number; //小题类型1:单选2:多选3:判断4:主观5:填空6:阅读理解7:以后都是自定义题型
    description: string; //小题的题目
    contentText: string; //原题目
    analysis: string; //主观题答案
    parentId: number; //父ID
    paperId: number; //活动ID
    paperType: number; //活动类型0:练习1:作业
    createUser: number; //创建人ID
    updateUser: null; //修改人ID
    createTime: string; //创建时间
    score?: string; //分数
    sort: string; //排序
    queTypeName: string; //题型名称
    optionModelList: optionModelListItem[];
}

export interface QuestionListItem {
    id: number; //大题ID
    queTypeId: number; //大题的类型
    description: string; //大题描述(题目)
    contentText: string; //原题目
    analysis: string; //主观题答案
    paperId: number; //活动id
    paperType: number; //活动类型0:练习1:作业
    createTime: string; //创建时间
    createUser: number; //创建人
    score?: string; //分数
    sort: string; //排序
    queTypeName: string; //题型名称
    optionModelList: optionModelListItem[];
    questionModelList: questionModelListItem[];
    index: number;
}

/**
 * 单选题类型守护
 * @param data
 */
export function isSingleChoiceType(
    data: QuestionType
): data is SingleChoiceType {
    return data.queTypeId === BaseQuestionType.SingleChoice;
}

/**
 * 多选题类型守护
 * @param data
 */
export function isMultipleChoiceType(
    data: QuestionType
): data is MultipleChoiceType {
    return data.queTypeId === BaseQuestionType.MultipleChoice;
}

/**
 * 判断题类型守护
 * @param data
 */
export function isJudgeType(data: QuestionType): data is JudgeType {
    return data.queTypeId === BaseQuestionType.Judge;
}

/**
 * 填空类型守护
 * @param data
 */
export function isFillType(data: QuestionType): data is FillType {
    return data.queTypeId === BaseQuestionType.Fill;
}

/**
 * 主观题类型守护
 * @param data
 */
export function isSubjectiveType(data: QuestionType): data is SubjectiveType {
    return data.queTypeId === BaseQuestionType.Subjective;
}

/**
 * 主观题类型守护
 * @param data
 */
export function isMixedType(data: QuestionType): data is MixedType {
    return data.queTypeId >= BaseQuestionType.Mixed;
}

/**
 *
 * @param exerciseId 试题列表
 */
export function getQuestionList(exerciseId: string) {
    return axios
        .post<SuccessData<QuestionListItem[]>>(
            `/teacher/exercise/getQuestionList`,
            `exerciseId=${exerciseId}`
        )
        .then(res => res.data.data)
        .then(data =>
            data.map((item: QuestionListItem, index: number) => ({
                ...item,
                index: index + 1
            }))
        );
}

/**
 * 把服务端数据转成前端试题类型
 * @param data
 */
export function convertQuestion(data: QuestionListItem): QuestionType {
    const queTypeId = data.queTypeId;

    const question: QuestionType = {
        id: String(data.id),
        contentText: data.contentText,
        options: [],
        score: toNumber(data.score),
        queTypeId: data.queTypeId,
        paperId: String(data.paperId),
        sort: toNumber(data.sort)
    };

    if (
        [
            BaseQuestionType.SingleChoice,
            BaseQuestionType.MultipleChoice
        ].includes(queTypeId)
    ) {
        return { ...question, options: convertOptions(data.optionModelList) };
    }

    if (queTypeId === BaseQuestionType.Judge) {
        return {
            ...question,
            judgeOptions: convertOptions(data.optionModelList)
        };
    }

    if (queTypeId === BaseQuestionType.Fill) {
        return { ...question, blanks: convertOptions(data.optionModelList) };
    }

    if (queTypeId === BaseQuestionType.Subjective) {
        return { ...question, analysis: data.analysis };
    }

    const questions = data.questionModelList.map(data =>
        convertQuestion(data as any)
    ) as SimpleQuestionType[];
    return { ...question, questions };
}

/**
 * 把前端试题格式转换成服务器格式
 */
function serializeQuestions(data: QuestionType, toJson = true) {
    const params: any = { ...data }; // 不直接修改data, 防止引起引用错误

    if ("options" in params) {
        params.options = params.options.map((option: any) => ({
            ...option,
            isRight: option.isRight ? "1" : "0"
        }));
        if (toJson) params.options = JSON.stringify(params.options);
    }

    if ("judgeOptions" in params) {
        params.options = params.judgeOptions.map((option: any) => ({
            ...option,
            isRight: option.isRight ? "1" : "0"
        }));
        if (toJson) params.options = JSON.stringify(params.options);
        delete params.judgeOptions;
    }

    if ("blanks" in params) {
        params.options = params.blanks.map((option: any) => ({
            ...option,
            description: option.description,
            isRight: "1"
        }));
        if (toJson) params.options = JSON.stringify(params.options);
        delete params.blanks;
    }

    if ("questions" in params) {
        if (params.questions.length === 0) throw new Error("还没有添加小题!");
        if (toJson)
            params.questions = JSON.stringify(
                params.questions.map((data: QuestionType, index: number) => ({
                    ...serializeQuestions(data, false),
                    sort: index
                }))
            );
    }

    return params;
}

/**
 * 把服务端数据转成前端试题类型
 * @param type
 * @param options
 */
function convertOptions(options: optionModelListItem[]) {
    return options.map(item => ({
        ...item,
        key: item.id,
        isRight: item.isRight === 1
    })) as unknown as OptionsType[];
}

/**
 * 校验试题格式
 * @param data
 */
export function checkQuestion(data: QuestionType) {
    // 校验主观题选项
    if (
        isMultipleChoiceType(data) &&
        data.options.filter(item => item.isRight).length < 2
    ) {
        throw new Error("题目添加失败，请至少选择两个正确答案!");
    }

    // 校验混合题型小题数量
    if (isMixedType(data) && data.questions.length === 0) {
        throw new Error("还没有添加小题!");
    }

    // 后续有别的校验项再说
}

/**
 * 添加或修改试题
 * @param data
 */
export function saveQuestion(data: QuestionType) {
    const params = serializeQuestions(data);
    return axios.post("/teacher/exercise/saveQuestion", qs.stringify(params));
}

export interface ExerciseItem {
    id: number;
    description: string;
    exerciseStatus: number;
    validityTime: any;
    weekTimeId: number;
    startTime: any;
    endTime: any;
    delStatus: number;
    createUser: number;
    updateUser: number;
    createTime: string;
    updateTime: string;
    exerciseCount: number;
    exerciseAllCount: number;
    judgeCount: number;
    noJoinInExerciseCount: number;
    noJudgeCount: number;
}

/**
 * 获取练习活动列表数据
 * @param weekTimeId
 */
export function getExerciseList(weekTimeId: number) {
    return axios
        .post<SuccessData<ExerciseItem[]>>(
            `/teacher/exercise/getExerciseList`,
            qs.stringify({ weekTimeId })
        )
        .then(data => data.data.data);
}

interface ExerciseParams {
    weekTimeId: number;
    description: string;
    validityTime: number;
    id?: number | "";
}

/**
 * 添加/修改练习
 * @param params
 */
export function saveExercise(params: ExerciseParams) {
    return axios
        .post(`/teacher/exercise/saveExercise`, qs.stringify({ ...params }))
        .then(data => data.data);
}

export interface ExerciseSettings {
    id: number;
    weekTimeId: number;
    validityTime: number;
    startTime: string;
    endTime: string;
    description: string;
}

/**
 * 设置活动返显
 * @param id
 */
export function getExercise(id: number) {
    return axios
        .post<SuccessData<ExerciseSettings>>(
            `/teacher/exercise/getExercise`,
            qs.stringify({ id })
        )
        .then(data => data.data.data);
}

export enum ExerciseStatus {
    NoExercise = -1, // 未添加题目
    NotStarted = 0, // 有题目未开始
    doing = 1, // 进行中
    done = 2, // 已结束
    del = 3 // 已删除
}

export interface TopicItem {
    id: string;
    description: string; //题型名称
    shareStatus: string; //1基本类型，0新增类型
}

export interface TopicItemWithSubTypes extends TopicItem {
    basicTypeIds: string;
}

/**
 * 题型管理列表
 */
export function getTopicTypeList() {
    return axios
        .post<SuccessData<TopicItemWithSubTypes[]>>(
            `/teacher/exercise/getTopicTypeList`
        )
        .then(res => res.data.data);
}

/**
 * 添加题型列表
 */
export function getAddTopicTypeList() {
    return axios
        .post<SuccessData<TopicItem[]>>(`/teacher/exercise/getAddTopicTypeList`)
        .then(res => res.data.data);
}

export interface TopicTypeProp {
    id: string; //题型ID
    description: string; //题型名称
    extendStatus: string; //基本题型:0不是1:是
    basicTypeIds: string; //拥有小题的题型
    shareStatus: string; //默认题型0:不是默认的1:默认的
    createUser: string;
    updateUser: string;
    createTime: string;
    updateTime: string;
    isHaveQuestion: string; //题型下面是否有题0:没有1:有
}

/**
 * 编辑自定义题型（反显）
 *
 */
export function getTopicType(topicTypeId: string) {
    return axios
        .post<SuccessData<TopicTypeProp>>(
            `/teacher/exercise/getTopicType`,
            qs.stringify({ topicTypeId })
        )
        .then(res => res.data.data);
}

/**
 * 添加/修改练习
 * @param description 题型名称
 * @param basicTypeIds 添加的题型id 多个用逗号拼接
 * @param id(新增不传修改传)
 */
export function saveTopicType(
    id: string,
    description: string,
    basicTypeIds: string
) {
    return axios
        .post(
            `/teacher/exercise/saveTopicType`,
            qs.stringify({ basicTypeIds, description, id })
        )
        .then(res => res.data.data);
}

/**
 * 顺序修改保存
 * @param topicTypeIds 示例8,4,3,2,1,7,6,5 多个题型ID
 */
export function managerTopicTypeSort(topicTypeIds: string) {
    return axios
        .post(
            `/teacher/exercise/managerTopicTypeSort`,
            qs.stringify({ topicTypeIds })
        )
        .then(res => res.data.data);
}

export interface CourseInfoItem {
    id: number; //课程id
    customerCode: string; //租户code
    batchNo: string; //学期
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    courseName: string; //课程名称
    teacherId: number; //教师ID
}

/**
 * 课程信息下拉列表
 */

export function getQuestionCourseInfo() {
    return axios
        .post<SuccessData<CourseInfoItem[]>>(
            `/teacher/exercise/getQuestionCourseInfo`
        )
        .then(res => res.data.data);
}

export interface QuestionBaseList {
    rowNo: number;
    questionId: number; //试题ID
    batchName: string; //学期
    courseName: string; //课程名称
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    queTypeDescription: string; //题型
    questionDescription: string; //题目
    paperType: string; //试卷类别:0：练习1:作业
    createTime: string; //创建时间
    updateTime: string; //修改时间
    exerciseId: number; //活动ID
}

export interface QuestionDatabaseItem {
    allCount: number;
    list: QuestionBaseList[];
}

interface QuestionParams {
    search: string;
    pageSize: number;
    currPage: number;
    exerciseId: number;
    sortName: string;
    sortType: string;
    courseId: string;
    queTypeId: string;
}

/**
 * 题库列表
 *
 * @param params
 */

export function getQuestionDatabaseList(params: QuestionParams) {
    const token = getToken();
    return axios
        .post<SuccessData<QuestionDatabaseItem>>(
            `/teacher/exercise/getQuestionDatabaseList`,
            qs.stringify(params),
            { headers: { token } }
        )
        .then(res => res.data.data);
}

export function copyQuestion(questions: string, exerciseId: string) {
    const token = getToken();
    return axios.post(
        `/teacher/exercise/copyQuestion`,
        qs.stringify({ questions, exerciseId }),
        { headers: { token } }
    );
}

/**
 * 试题排序 大题
 */

export function saveBigQuestionSort(exerciseId: string, questionIds: string) {
    const token = getToken();
    return axios.post(
        `/teacher/exercise/saveBigQuestionSort`,
        qs.stringify({ exerciseId, questionIds }),
        { headers: { token } }
    );
}

/**
 * 删除当前题
 */
export function removeQuestion(exerciseId: string, questionId: string) {
    const token = getToken();
    return axios.post(
        `/teacher/exercise/removeQuestion`,
        qs.stringify({ exerciseId, questionId }),
        { headers: { token } }
    );
}
/**
 * 开始练习
 * @param weekTimeId
 * @param exerciseId
 */
export function publishExercise(weekTimeId: number, exerciseId: number) {
    return axios
        .post(
            `/teacher/exercise/publishExercise`,
            qs.stringify({ weekTimeId, exerciseId })
        )
        .then(data => data.data.data);
}

export interface ExerciseInfo {
    exerciseId: number;
    exerciseName: string;
    validityTime: string;
    startTime: string;
    endTime: string;
    createTime: string;
    useTime: string;
    questionModels: ExerciseQuestionItem[];
    exerciseStatus: ExerciseStatus;
}

export interface ExerciseQuestionItem {
    id: number;
    queTypeId: BaseQuestionType;
    description: string;
    paperId: number;
    paperType: number;
    score: number | null;
    queTypeName: string;
    contentText: string;
}

/***
 * 开启活动后的统计(答题卡和活动信息)
 * @param exerciseId
 */
export function getExerciseInfo(exerciseId: number) {
    return axios
        .post<SuccessData<ExerciseInfo>>(
            `/teacher/exercise/getExerciseInfo`,
            qs.stringify({ exerciseId })
        )
        .then(data => data.data.data);
}

export interface StuInfo {
    studentId: string;
    stuStatus: string;
    name: string;
}

export interface StuJoinInfo {
    courseCount: number;
    joinInCount: number;
    noJoinInCount: number;
    list: StuInfo[];
}

/**
 * 进行中的活动实时参与信息
 * @param exerciseId
 * @param courseId
 */
export function getStuJoinInExerciseList(exerciseId: number, courseId: string) {
    return axios
        .post<SuccessData<StuJoinInfo>>(
            `/teacher/exercise/getStuJoinInExerList`,
            qs.stringify({ exerciseId, courseId })
        )
        .then(data => data.data.data);
}

interface CopyExerciseParams {
    exerciseId: number;
    search: string;
    courseId: string;
    sortType: string;
    pageSize: number;
    currPage: number;
}

export interface CopyExerciseListItem {
    rowNo: number;
    exerciseId: number;
    batchNo: string;
    customerCode: string;
    courseId: number;
    courseNo: string;
    courseSeq: string;
    courseName: string;
    exerciseName: string;
    weekTimeInfo: string;
    weekTimeId: number;
    questionCount: number;
    createTime: string;
    createUser: number;
}

export interface CopyExerciseList {
    allCount: number;
    list: CopyExerciseListItem[];
}

/**
 * 复制练习列表
 */

export function getCopyExerciseList(params: CopyExerciseParams) {
    return axios
        .post<SuccessData<CopyExerciseList>>(
            `/teacher/exercise/getCopyExerciseList`,
            qs.stringify(params)
        )
        .then(res => res.data.data);
}

/**
 * 复制练习提交
 */
export function copyExercise(currExerciseId: string, copyExerciseId: string) {
    return axios
        .post<SuccessData<{ data: number }>>(
            `/teacher/exercise/copyExercise`,
            qs.stringify({ currExerciseId, copyExerciseId })
        )
        .then(res => res.data.data);
}

export interface getMarkListItem {
    score: string; // 总得分
    submitTime: string; // 提交时间
    name: string; // 学生姓名
    index: string; // 序号
    markStatus: string; // 批阅状态 1:已批阅, 0:未批阅
    subScore: string; // 主观题得分
    className: string; // 班级
    studentNo: string; // 学生学号
    joinStatus: string; // 参与状态 1:已参与, 0:未参与
    objScore: string; // 客观题得分
    paperFlags: BooleanValue;
}

interface ExerciseDescribe {
    validityTime: string;
    description: string;
    startTime: string;
    endTime: string;
}

export interface ListsItem {
    score: string; // 总得分
    submitTime: string; // 提交时间
    name: string; // 学生姓名
    index: string; // 序号
    markStatus: string; // 批阅状态 1:已批阅, 0:未批阅
    subScore: string; // 主观题得分
    className: string; // 班级
    studentNo: string; // 学生学号
    joinStatus: string; // 参与状态 1:已参与, 0:未参与
    objScore: string; // 客观题得分
}

interface MarkListItem {
    markNo: string;
    joinPercent: string;
    exercise: ExerciseDescribe;
    studentNo: string;
    list: ListsItem[];
    unMarkNo: string;
    unJoinNo: string;
}
/**
 * 批阅列表
 */
export function getMarkList(exerciseId: string) {
    return axios
        .get<SuccessData<MarkListItem>>(
            `/teacher/exercise/getMarkList?exerciseId=${exerciseId}`
        )
        .then(res => res.data.data);
}

/**
 * 是否可以收卷
 */
function getCollectPaperFlag(exerciseId: string, studentNos: string[]) {
    return axios
        .post<SuccessData<Record<string, BooleanValue>>>(
            "/teacher/exercise/getCollectPaperFlag",
            qs.stringify({ exerciseId, studentNo: studentNos.join() })
        )
        .then(res => res.data.data);
}

/**
 * 批阅列表, 附带是否可以收卷状态
 */
export async function getMarkListWithPaperFlag(exerciseId: string) {
    const { list, ...restData } = await getMarkList(exerciseId);

    const studentNos = list.map(item => item.studentNo);
    const paperFlags = await getCollectPaperFlag(exerciseId, studentNos);

    const newList = list.map(item => ({
        ...item,
        paperFlags: isTrue(paperFlags[item.studentNo])
    }));

    return { ...restData, list: newList };
}

/**
 * 收卷
 * @param exerciseId
 * @param studentNo
 * @param courseId
 */
export function collectExaminationPaper(
    exerciseId: string,
    studentNo: string,
    courseId: string
) {
    return axios.post(
        "/teacher/exercise/collectExaminationPaper",
        qs.stringify({ exerciseId, studentNo, courseId })
    );
}

export interface OptionsItem {
    isRight: number;
    questionId: number;
    description: string;
    id: number;
    serialNum: string;
}

export interface viewPaperListItem {
    score: string; // 本题分值
    style: string; // 题目是基础题型还是多题干 1:基础题型(单题干), 2:多题干
    questions: viewPaperListItem[];
    id: number;
    sort: number;
    stuFinishTopic: string; // 学生本题是否作答 1:本题已作答完成, 0:本题未作答
    queTypeId: number;
    paperId: string;
    contentText: string;
    stuAns: string;
    analysis: string;
    teacherMark: string;
    stuIsRight: string;
    stuScore: number;
    typeName: string;
    options: OptionsItem[];
    index: number;
    teacherComment: string;
    key: string;
}

export interface viewPaperObj {
    stuDoFlg: string; //学生试卷是否作答标识
    unfinishedCount: number; //学生未作答试题数
    topicCount: string; //试题数
    stuPaperScore: string; //学生试卷得分
    stuPaperObjScore: string; //客观题得分
    stuPaperSubScore: string; //主观题得分
    totalScore: string; //试卷总分
    stuSubmitTime: string; //提交时间
    topics: viewPaperListItem[]; //试题列表
}

//获取题目正确答案
export function getRightOption(queTypeId: number, options: any[]) {
    //单选，多选题正确答案：选项（A,B....)
    if (
        [
            BaseQuestionType.SingleChoice,
            BaseQuestionType.MultipleChoice
        ].includes(queTypeId)
    ) {
        return options
            .filter(item => item.isRight === 1)
            .map(item => item.serialNum);
    }

    //判断题正确答案：正确/错误
    if (queTypeId === BaseQuestionType.Judge) {
        return options
            .filter(item => item.isRight === 1)
            .map(item => item.description);
    }
}

// 获得学生答案
export function getStuAnswer(
    queTypeId: number,
    stuAns: any,
    options: OptionsItem[]
) {
    //单/多选
    if (
        [
            BaseQuestionType.SingleChoice,
            BaseQuestionType.MultipleChoice
        ].includes(queTypeId)
    ) {
        return options
            .filter((item: OptionsItem) => stuAns.includes(item.id + ""))
            .map(item => item.serialNum)
            .join();
    }

    //判断
    if (queTypeId === BaseQuestionType.Judge) {
        return options
            .filter((item: OptionsItem) => stuAns.includes(item.id + ""))
            .map((item: any) => item.description)
            .join();
    }

    //填空
    if (queTypeId === BaseQuestionType.Fill) {
        if (stuAns !== "") {
            const answer = JSON.parse(stuAns);
            const answerObj: { [key: string]: any } = {};

            options.forEach((item: OptionsItem, index: number) => {
                if (_.has(answer, item.id + "")) {
                    answerObj[`answer${index + 1}`] = answer[item.id + ""];
                } else {
                    answerObj[`answer${index + 1}`] = "";
                }
            });
            return answerObj;
        }
        return stuAns;
    }
}

/**
 * 打开学生试卷
 */
export function viewPaper(exerciseId: string, studentNo: string) {
    const token = getToken();
    return axios
        .get<SuccessData<viewPaperObj>>(
            `/teacher/exercise/viewPaper?exerciseId=${exerciseId}&studentNo=${studentNo}`,
            { headers: { token } }
        )
        .then(res => res.data.data)
        .then(data => ({
            ...data,
            topics: data?.topics
                ? data.topics.map((item: viewPaperListItem, index: number) => ({
                      ...item,
                      index: index + 1,
                      key: studentNo + item.id,
                      options: item?.options
                          ? item.options.map((option, index) => ({
                                ...option,
                                serialNum: String.fromCharCode(index + 65)
                            }))
                          : [],
                      questions: item?.questions
                          ? item.questions.map(
                                (item: viewPaperListItem, index: number) => ({
                                    ...item,
                                    index: index + 1,
                                    options: item?.options
                                        ? item.options.map((option, index) => ({
                                              ...option,
                                              serialNum: String.fromCharCode(
                                                  index + 65
                                              )
                                          }))
                                        : []
                                })
                            )
                          : []
                  }))
                : []
        }));
}

/**
 * 结束练习
 * @param exerciseId
 * @param weekTimeId
 */
export function endExercise(exerciseId: number, weekTimeId: number) {
    return axios.post(
        `/teacher/exercise/endExercise`,
        qs.stringify({ exerciseId, weekTimeId })
    );
}

/**
 * 删除练习
 * @param exerciseId
 */
export function removeExercise(exerciseId: number) {
    return axios.post(
        `/teacher/exercise/removeExercise`,
        qs.stringify({ exerciseId })
    );
}

export interface EndExerciseStuInfo {
    studentId: string; // 学号
    stuName: string; // 姓名
}

export interface EndExerciseOptionModel {
    id: number; // 选项ID
    questionId: number; // 题ID
    description: string; // 选项描述
    isRight: BooleanValue; // 是否正确
    stuOptionInfos?: EndExerciseStuInfo[]; // 选择选项的学生信息
}

export interface EndExerciseQuestion {
    id: number;
    paperId: number;
    paperType: number;
    queTypeId: number;
    queTypeName: string;
    score: string;
    description: string;
    contentText: string;
    analysis: string;
    optionModelList: EndExerciseOptionModel[];
    questionModelList: EndExerciseQuestion[];
    accuracy: string; // 正确率
    stuRightList?: EndExerciseStuInfo[];
    stuUnRightList?: EndExerciseStuInfo[];
}

interface EndExerciseInfo {
    joinPercent: string;
    stuCount: number; // 学生总数
    questionList: EndExerciseQuestion[];
}

/**
 * 练习结束详情统计
 * @param exerciseId
 */
export function getEndExerciseInfo(exerciseId: number) {
    return axios
        .post<SuccessData<EndExerciseInfo>>(
            "/teacher/exercise/getEndExerciseInfo",
            qs.stringify({ exerciseId })
        )
        .then(res => res.data.data);
}

export interface TopExerciseInfo {
    exerciseId: 260;
    createTime: string;
    endTime: string;
    exerciseName: string;
    startTime: string;
    useTime: string;
    validityTime: string;
}

/**
 * 获取练习信息
 */
export function getTopExerciseInfo(exerciseId: number) {
    return axios
        .post<SuccessData<TopExerciseInfo>>(
            "/teacher/exercise/getTopExerciseInfo",
            qs.stringify({ exerciseId })
        )
        .then(res => res.data.data);
}

export interface markScoreParams {
    studentNo: string;
    exerciseId: string;
    questionId: number;
    score?: number | null;
    comment?: string;
}

export function saveMarkScore(params: markScoreParams) {
    const token = getToken();
    return axios.post("/teacher/exercise/saveMarkScore", qs.stringify(params), {
        headers: { token }
    });
}

export interface ExerciseStudentItem {
    studentId: string;
    flag: "1" | "0"; // 是否参加 0否，1是
    name: string;
}

/**
 * 重新发布选择列表
 * @param id
 * @param weekTimeId
 */
export function anewExerciseStudentList(id: number, weekTimeId: number) {
    return axios
        .post<SuccessData<ExerciseStudentItem[]>>(
            "/teacher/exercise/anewExerciseStudentList",
            qs.stringify({ id, weekTimeId })
        )
        .then(res => res.data.data);
}

/**
 * 重新发布
 * @param data
 */
export function anewPublishExercise(data: {
    id: number;
    weekTimeId: number;
    validityTime: string;
    studentIds: string[];
}) {
    const { studentIds, ...formData } = data;

    return axios.post(
        "/teacher/exercise/anewPublishExercise",
        qs.stringify({ ...formData, studentIds: studentIds?.join(",") })
    );
}

export interface ExercisePublishResume {
    validityTime: string;
    startTime: string;
    endTime: string;
}

/**
 * 练习发布履历
 * @param id
 */
export function exercisePublishResume(id: number) {
    return axios
        .get<SuccessData<ExercisePublishResume[]>>(
            `/teacher/exercise/exercisePublishResume?id=${id}`
        )
        .then(res => res.data.data);
}

/**
 * 获取问题详情
 * @param questionId 问题id
 */
export function getExerciseQuestionInfo(questionId: number) {
    return axios
        .post<SuccessData<QuestionItem>>(
            "/teacher/exercise/getExerciseQuestionInfo",
            qs.stringify({ questionId })
        )
        .then(res => res.data.data);
}

export interface UrlParams {
    studentNo: string;
    exerciseId: string;
}
